import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, KeyRound, Mail, UserPlus, Building2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { usePaged, useUsers } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { Pager } from "@/components/core/Pager";
import { ClientFormDialog } from "@/components/core/ClientFormDialog";
import { DICT } from "@/site/i18n";

const STATI = [["nuova", "Nuova", "bg-[#E8173A] text-white"], ["in_lavorazione", "In lavorazione", "bg-amber-300 text-[#0A1128]"], ["chiusa", "Chiusa", "bg-emerald-600 text-white"]];
const statoOf = (v) => STATI.find((s) => s[0] === v) || STATI[0];
const notifyCount = () => window.dispatchEvent(new Event("core-requests-changed"));

const Sel = ({ value, onChange, items, testid, className = "w-48" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`rounded-xl bg-[#A6DAFC] ${className}`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const RequestDialog = ({ r, onClose, onSaved, users }) => {
  const [f, setF] = useState({});
  const [client, setClient] = useState(false);
  useEffect(() => { if (r) setF({ stato: r.stato, assignee_id: r.assignee_id || "none", note: r.note || "" }); }, [r]);
  if (!r) return null;
  const save = async (extra = {}) => {
    try {
      const res = await api.patch(`/sito/richieste/${r.id}`, { ...f, ...extra });
      onSaved(res.data); notifyCount(); toast.success("Richiesta aggiornata");
      return res.data;
    } catch (e) { toast.error(errMsg(e)); }
  };
  const initial = { ragione_sociale: r.azienda || r.nome || "", referente: r.nome || "", email: r.email, telefono: r.telefono || "", note: r.messaggio || "" };
  return (
    <>
      <Dialog open={!!r && !client} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#A6DAFC] border-[#5FB3EA] rounded-2xl" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{r.tipo === "link_portale" ? "Richiesta link portale" : r.nome}</DialogTitle>
            <DialogDescription>Ricevuta il {fmtDateTime(r.created_at)} · lingua {r.lingua === "en" ? "inglese" : "italiano"}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 text-sm text-[#0A1128]">
            {r.azienda && <div className="flex items-center gap-2"><Building2 className="h-4 w-4" />{r.azienda}</div>}
            <a href={`mailto:${r.email}`} className="flex items-center gap-2 font-bold text-[#E8173A]" data-testid="request-email"><Mail className="h-4 w-4" />{r.email}</a>
            {r.telefono && <div>Tel. {r.telefono}</div>}
            {!!r.servizi?.length && <div className="flex flex-wrap gap-1.5">{r.servizi.map((k) => <span key={k} className="rounded-full bg-white/70 px-2.5 py-1 text-xs font-bold">{DICT.it.servizi.items[k]?.[0] || k}</span>)}</div>}
            <div className="rounded-xl bg-white p-3 whitespace-pre-line" data-testid="request-message">{r.messaggio}</div>
            {r.tipo === "link_portale" && <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-xs">Il link NON è stato inviato in automatico. Verifica il cliente e invialo dalla scheda «Portale cliente».</div>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Sel value={f.stato || "nuova"} onChange={(v) => setF({ ...f, stato: v })} items={STATI} testid="request-stato" className="w-full" />
            <Sel value={f.assignee_id || "none"} onChange={(v) => setF({ ...f, assignee_id: v })} items={[["none", "Nessun socio"], ...(users || []).map((u) => [u.id, `${u.nome} ${u.cognome}`])]} testid="request-assignee" className="w-full" />
          </div>
          <Textarea value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} rows={4} placeholder="Note interne (visibili solo ai soci)" className="bg-white rounded-xl" data-testid="request-note" />
          <DialogFooter className="gap-2">
            {r.tipo === "contatto" && !r.client_id && <Button variant="outline" onClick={() => setClient(true)} className="rounded-full font-bold" data-testid="request-create-client"><UserPlus className="h-4 w-4 mr-1.5" />Crea cliente da richiesta</Button>}
            {r.client_id && <a href={`/clienti/${r.client_id}`} className="inline-flex h-10 items-center justify-center rounded-full border border-[#0A1128]/20 px-4 text-sm font-bold" data-testid="request-open-client">Apri cliente</a>}
            <Button onClick={async () => { if (await save()) onClose(); }} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="request-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ClientFormDialog open={client} onOpenChange={setClient} initial={initial} users={users || []}
        onSaved={async (c) => { setClient(false); await save({ client_id: c.id, stato: f.stato === "nuova" ? "in_lavorazione" : f.stato }); onClose(); }} />
    </>
  );
};

export const RequestsInbox = () => {
  const [sp] = useSearchParams();
  const [stato, setStato] = useState("all");
  const [tipo, setTipo] = useState("all");
  const { data: users } = useUsers();
  const paged = usePaged("/sito/richieste", { stato: stato === "all" ? undefined : stato, tipo: tipo === "all" ? undefined : tipo });
  const items = paged.data?.items;
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const rid = sp.get("r");
    if (rid && items && !open) { const x = items.find((i) => i.id === rid); if (x) setOpen(x); }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Sel value={stato} onChange={setStato} items={[["all", "Tutti gli stati"], ...STATI]} testid="requests-filter-stato" />
        <Sel value={tipo} onChange={setTipo} items={[["all", "Tutti i tipi"], ["contatto", "Contatti dal sito"], ["link_portale", "Richieste link portale"]]} testid="requests-filter-tipo" />
      </div>
      <Panel className="overflow-hidden" data-testid="requests-list">
        {items && !items.length ? <EmptyState icon={Inbox} title="Nessuna richiesta" text="Le richieste inviate dal sito compariranno qui." testid="requests-empty" />
          : (items || []).map((r) => {
            const [, l, cls] = statoOf(r.stato);
            return (
              <button key={r.id} onClick={() => setOpen(r)} className="w-full text-left flex flex-wrap items-center gap-3 px-4 py-3.5 border-b border-[#5FB3EA]/50 last:border-0 hover:bg-[#8FD0FB]/50 transition-colors" data-testid={`request-row-${r.id}`}>
                <div className="h-10 w-10 rounded-full bg-[#0A1128] text-white flex items-center justify-center shrink-0">{r.tipo === "link_portale" ? <KeyRound className="h-4 w-4" /> : <Mail className="h-4 w-4" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#0A1128] truncate">{r.tipo === "link_portale" ? "Richiesta link portale" : `${r.nome}${r.azienda ? ` · ${r.azienda}` : ""}`}</div>
                  <div className="text-xs text-slate-600 truncate">{r.email} · {fmtDateTime(r.created_at)}{r.assignee_nome ? ` · ${r.assignee_nome}` : ""}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${cls}`} data-testid={`request-stato-${r.id}`}>{l}</span>
              </button>
            );
          })}
      </Panel>
      <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="requests-pager" />
      <RequestDialog r={open} users={users} onClose={() => setOpen(null)} onSaved={(x) => { setOpen(x); paged.reload(); }} />
    </>
  );
};
